import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/authContext";
import { Link, useNavigate } from "react-router-dom";
import { FaGoogle, FaLock, FaUser } from "react-icons/fa";
import { doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";

import logo from "../assets/Logo.png";
import Back from "../assets/Back.png";
import { db, auth } from "../firebase";
import PopupModal from "./PopupModal";

export function Login() {
  const emailRef = useRef(null);
  const navigate = useNavigate();

  const {
    GuardarEnLocal,
    login,
    loginWithGoogle,
    resetPassword,
    sincronizarUsuarioYDispositivo,
  } = useAuth();

  const [user, setUser] = useState({
    email: "",
    password: "",
  });

  const [popup, setPopup] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
  });

  const traducirError = (error) => {
    switch (error.code) {
      case "auth/invalid-email":
        return "⚠️ El email no es válido. Revisa el formato.";

      case "auth/user-not-found":
        return "❌ No encontramos ninguna cuenta con ese correo.";

      case "auth/wrong-password":
        return "🔑 La contraseña es incorrecta. Vuelve a intentarlo.";

      case "auth/invalid-credential":
        return "🔑 El email o la contraseña no son correctos.";

      case "auth/too-many-requests":
        return "🚫 Demasiados intentos fallidos. Espera un momento antes de volver a intentarlo.";

      case "auth/missing-email":
        return "📧 Escribe tu correo antes de continuar.";

      case "auth/popup-closed-by-user":
        return "❌ Has cerrado el acceso con Google.";

      default:
        return (
          error?.message ||
          "⚠️ Algo salió mal al iniciar sesión. Revisa tu email y contraseña."
        );
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      emailRef.current?.focus();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const handleChange = ({ target: { name, value } }) => {
    setUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ------------------ LOGIN CON EMAIL ------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    setPopup({
      show: false,
      type: "info",
      title: "",
      message: "",
    });

    try {
      const userCredential = await login(
        user.email,
        user.password
      );

      const uid = userCredential.user.uid;

      // Validar que existe en colección "usuarios"
      const userRef = doc(db, "usuarios", uid);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        await signOut(auth);

        throw new Error(
          "🚫 Tu cuenta no está registrada como usuario válido."
        );
      }

      // Usar el nombre de Firestore si existe
      const nombre =
        snap.data().nombre ||
        userCredential.user.displayName ||
        "Usuario";

      await sincronizarUsuarioYDispositivo(
        uid,
        nombre,
        user.email
      );

      await GuardarEnLocal(uid);

      localStorage.setItem("PuntosSesion", 0);
      localStorage.setItem("PuntosTotales", 0);

      navigate("/Comprobacion");
    } catch (error) {
      console.error("❌ Error login:", error);

      setPopup({
        show: true,
        type: "error",
        title: "No hemos podido iniciar sesión",
        message: traducirError(error),
      });
    }
  };

  // ------------------ LOGIN CON GOOGLE ------------------
  const handleGoogleSignin = async () => {
    try {
      const userCredential = await loginWithGoogle();

      const uid = userCredential.user.uid;
      const email = userCredential.user.email;

      // Recargar datos para obtener displayName
      await auth.currentUser.reload();

      const displayName =
        auth.currentUser.displayName || "Usuario";

      console.log(
        "✅ Google Sign-In → displayName tras reload:",
        displayName
      );

      await sincronizarUsuarioYDispositivo(
        uid,
        displayName,
        email
      );

      await GuardarEnLocal(uid);

      localStorage.setItem("PuntosSesion", 0);
      localStorage.setItem("PuntosTotales", 0);

      navigate("/Comprobacion");
    } catch (error) {
      console.error(
        "❌ Error login con Google:",
        error
      );

      setPopup({
        show: true,
        type: "error",
        title: "No hemos podido acceder con Google",
        message: traducirError(error),
      });
    }
  };

  // ------------------ RESET PASSWORD ------------------
  const handleResetPassword = async () => {
    if (!user.email) {
      setPopup({
        show: true,
        type: "error",
        title: "Escribe tu correo",
        message:
          "Introduce primero tu email para enviarte el enlace de recuperación.",
      });

      return;
    }

    try {
      const actionCodeSettings = {
        url: "https://rpm.coachcycling.com/reset-password",
        handleCodeInApp: true,
      };

      await resetPassword(
        user.email,
        actionCodeSettings
      );

      setPopup({
        show: true,
        type: "success",
        title: "Revisa tu correo",
        message:
          "✅ Te hemos enviado un enlace para cambiar tu contraseña.",
      });
    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        title: "No hemos podido enviar el email",
        message: traducirError(error),
      });
    }
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-y-auto bg-black text-white"
      style={{
        backgroundImage: `url(${Back})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Oscurecer fondo */}
      <div className="absolute inset-0 bg-black/65" />

      {/* Degradado para mejorar lectura */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/95" />

      {/* Luces decorativas */}
      <div className="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-lime-400/10 blur-3xl" />

      <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-green-500/10 blur-3xl" />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-4 py-8 lg:px-8">
        <div className="grid w-full items-center gap-8 lg:grid-cols-2 lg:gap-14">

          {/* MENSAJE DE MARCA */}
          <section className="text-center lg:text-left">
            <img
              src={logo}
              alt="CoachCycling"
              className="mx-auto w-36 lg:mx-0 lg:w-44"
            />

            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-lime-400/30 bg-lime-400/10 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-lime-300">
              <span className="h-2 w-2 animate-pulse rounded-full bg-lime-400" />
              Tu sesión te espera
            </div>

            <h1 className="mt-5 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
              Vuelve a
              <span className="block text-lime-400">
                superar tus límites
              </span>
            </h1>

            <p className="mx-auto mt-5 max-w-lg text-base leading-7 text-gray-300 lg:mx-0 lg:text-lg">
              Entra en tu cuenta, conecta con tu equipo y
              sigue sumando kilómetros, puntos y progreso.
            </p>

            {/* Beneficios */}
            <div className="mt-7 grid grid-cols-3 gap-2 sm:gap-3">
              <div className="rounded-2xl border border-white/10 bg-black/35 p-3 text-center backdrop-blur-md">
                <div className="text-2xl">⚡</div>

                <p className="mt-2 text-xs font-bold text-white">
                  Acceso rápido
                </p>

                <p className="mt-1 text-[10px] text-gray-400">
                  Entra en segundos
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/35 p-3 text-center backdrop-blur-md">
                <div className="text-2xl">🚴</div>

                <p className="mt-2 text-xs font-bold text-white">
                  Tus clases
                </p>

                <p className="mt-1 text-[10px] text-gray-400">
                  Pedalea en grupo
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/35 p-3 text-center backdrop-blur-md">
                <div className="text-2xl">🏆</div>

                <p className="mt-2 text-xs font-bold text-white">
                  Tu ranking
                </p>

                <p className="mt-1 text-[10px] text-gray-400">
                  Sigue compitiendo
                </p>
              </div>
            </div>
          </section>

          {/* FORMULARIO */}
          <section className="mx-auto w-full max-w-md">
            <div className="rounded-[28px] border border-white/15 bg-black/65 p-5 shadow-2xl backdrop-blur-xl sm:p-7">
              <div className="mb-6">
                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-lime-400">
                  Bienvenido de nuevo
                </p>

                <h2 className="mt-2 text-2xl font-black text-white">
                  Entra en CoachCycling
                </h2>

                <p className="mt-2 text-sm leading-6 text-gray-400">
                  Continúa donde lo dejaste.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                {/* EMAIL */}
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-300"
                  >
                    Correo electrónico
                  </label>

                  <div className="flex items-center rounded-2xl border border-white/10 bg-white/10 px-4 transition focus-within:border-lime-400/70 focus-within:bg-white/15 focus-within:ring-2 focus-within:ring-lime-400/20">
                    <FaUser className="mr-3 shrink-0 text-gray-400" />

                    <input
                      ref={emailRef}
                      id="email"
                      type="email"
                      name="email"
                      value={user.email}
                      placeholder="tuemail@gmail.com"
                      autoComplete="email"
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent py-3.5 text-white placeholder-gray-500 outline-none"
                    />
                  </div>
                </div>

                {/* CONTRASEÑA */}
                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label
                      htmlFor="password"
                      className="text-xs font-bold uppercase tracking-wider text-gray-300"
                    >
                      Contraseña
                    </label>

                    <button
                      type="button"
                      onClick={handleResetPassword}
                      className="text-xs font-semibold text-lime-400 transition hover:text-lime-300"
                    >
                      ¿La has olvidado?
                    </button>
                  </div>

                  <div className="flex items-center rounded-2xl border border-white/10 bg-white/10 px-4 transition focus-within:border-lime-400/70 focus-within:bg-white/15 focus-within:ring-2 focus-within:ring-lime-400/20">
                    <FaLock className="mr-3 shrink-0 text-gray-400" />

                    <input
                      id="password"
                      type="password"
                      name="password"
                      value={user.password}
                      placeholder="Tu contraseña"
                      autoComplete="current-password"
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent py-3.5 text-white placeholder-gray-500 outline-none"
                    />
                  </div>
                </div>

                {/* BOTÓN PRINCIPAL */}
                <button
                  type="submit"
                  className="w-full rounded-2xl bg-gradient-to-r from-lime-400 to-green-500 px-5 py-4 text-sm font-black uppercase tracking-wide text-black shadow-xl shadow-lime-500/20 transition hover:brightness-110 active:scale-[0.98]"
                >
                  Entrar y empezar a pedalear
                </button>
              </form>

              {/* SEPARADOR */}
              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-white/10" />

                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  o continúa con
                </span>

                <div className="h-px flex-1 bg-white/10" />
              </div>

              {/* GOOGLE */}
              <button
                type="button"
                onClick={handleGoogleSignin}
                className="flex w-full items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white px-5 py-3.5 font-bold text-black shadow-lg transition hover:bg-gray-100 active:scale-[0.98]"
              >
                <FaGoogle />
                Continuar con Google
              </button>

              {/* REGISTRO */}
              <div className="mt-6 rounded-2xl border border-lime-400/20 bg-lime-400/10 p-4 text-center">
                <p className="text-sm text-gray-300">
                  ¿Todavía no tienes cuenta?
                </p>

                <p className="mt-1 text-xs text-gray-400">
                  Puedes empezar a pedalear gratis.
                </p>

                <Link
                  to="/register"
                  className="mt-3 inline-flex items-center justify-center rounded-xl bg-lime-400 px-5 py-2.5 text-sm font-black text-black transition hover:bg-lime-300"
                >
                  Crear mi cuenta gratis
                  <span className="ml-2">→</span>
                </Link>
              </div>

              {/* PRIVACIDAD */}
              <p className="mt-5 text-center text-[11px] leading-5 text-gray-500">
                Al acceder aceptas nuestra{" "}
                <a
                  href="https://coachcycling.com/privacy-policy-2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-gray-400 underline transition hover:text-white"
                >
                  política de privacidad
                </a>
                .
              </p>
            </div>

            <p className="mt-5 text-center text-xs italic text-gray-500">
              Tu mejor versión continúa aquí 🚴‍♂️🔥
            </p>
          </section>
        </div>
      </div>

      {/* POPUP */}
      <PopupModal
        show={popup.show}
        onClose={() =>
          setPopup((prev) => ({
            ...prev,
            show: false,
          }))
        }
        type={popup.type}
        title={popup.title}
        message={popup.message}
      />
    </div>
  );
}

export default Login;